import useToastContext from "hooks/useToastContext";
import React, { useState, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { apiPost } from "utils/apiFetch";
import pathObj from "utils/apiPath";
import OButton from "components/reusable/OButton";
import { validationRules } from "utils/constants";
import AuthContext from "context/AuthContext";
import { useTranslation } from "react-i18next";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { preventMaxInput } from "utils/validations";
import OInputField from "components/reusable/OInputField";
import { isEmpty } from "lodash";
import { motion } from "framer-motion";
import helpers from "utils/helpers";

const ChangePassword = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { errors, isDirty },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });

  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const notification = useToastContext();
  const { logoutUser, updatePageName } = useContext(AuthContext);
  const [icon, setIcon] = useState(true);
  const [icon2, setIcon2] = useState(true);
  const [icon3, setIcon3] = useState(true);



  const handleChange = async (data) => {
    try {
      if (!isDirty) {
        notification.info("Please enter something.");
        return;
      }
      setChangePasswordLoading(true);

      const res = await apiPost(pathObj.changePassword, {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      });
      if (res.data.success) {
        reset();
        notification.success(res?.data?.message);
        logoutUser();
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.error("err:", err);
    } finally {
      setChangePasswordLoading(false);
    }
  }






  const handleSubmitForm = async (data) => {
    helpers.alertFunction(
      `Are you sure you want to change your password ?`,
      data,
      handleChange,
      null,
      "Note: Changing your password will log you out of your current session. You will need to log in again using your new password."
    )
  };

  function changeIcon() {
    setIcon(!icon);
  }
  function changeIcon2() {
    setIcon2(!icon2);
  }
  function changeIcon3() {
    setIcon3(!icon3);
  }

  const newPassword = watch("newPassword");
  useEffect(() => {
    if (!isEmpty(newPassword)) {
      trigger("confirm_password");
    }
  }, [newPassword, trigger]);

  useEffect(() => {
    updatePageName("Change Password")
  }, [])
  return (
    <motion.div
      className="sm:px-8 px-4 py-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="border rounded-md shadow-md overflow-hidden xl:w-full dark:bg-white">
        <header className="border-b py-2 px-4 bg-gray-100 dark:bg-gray-800 h-[40px]">
          <div className="font-semibold dark:text-white">
            {t("CHANGE_PASSWORD")}
          </div>
        </header>
        <form onSubmit={handleSubmit(handleSubmitForm)} method="post">
          <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-cols-1">
            {/* OLD PASSWORD */}
            <div className="p-4">
              <div className="relative group">
                <OInputField
                  wrapperClassName="relative z-0 w-full group"
                  type={icon ? "password" : "text"}
                  name="oldPassword"
                  id="oldPassword"
                  autoFocus
                  placeholder={t("ENTER_OLD_PASSWORD")}
                  autoComplete="new-password"
                  inputLabel={
                    <>
                      {t("CHANGE_PASSWORD_OLD_PASSWORD")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  disableAutofill
                  errors={errors}
                  onInput={(e) => preventMaxInput(e, 15)}
                  register={register("oldPassword", {
                    required: "Please enter old password.",
                    validate: {
                      whiteSpace: (value) =>
                        value.trim()
                          ? true
                          : t("WHITE_SPACES_NOT_ALLOWED"),
                    },
                    pattern: {
                      value: validationRules.password,
                      message:
                        "Old password must contain lowercase, uppercase characters, numbers, special character and must be 8 characters long.",
                    },
                  })}
                />
                <span
                  className="password_view absolute top-[42px] right-[20px] cursor-pointer"
                  onClick={changeIcon}
                >
                  {icon ? (
                    <AiFillEyeInvisible className="text-gray-500 dark:text-white" />
                  ) : (
                    <AiFillEye className="text-gray-500 dark:text-white" />
                  )}
                </span>
              </div>
            </div>

            {/* NEW PASSWORD */}
            <div className="p-4">
              <div className="relative group">
                <OInputField
                  wrapperClassName="relative z-0 w-full group"
                  type={icon2 ? "password" : "text"}
                  name="newPassword"
                  id="newPassword"
                  placeholder={t("ENTER_NEW_PASSWORD")}
                  errors={errors}
                  inputLabel={
                    <>
                      {t("CHANGE_PASSWORD_NEW_PASSWORD")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  onInput={(e) => preventMaxInput(e, 15)}
                  register={register("newPassword", {
                    required: "Please enter new password.",
                    pattern: {
                      value: validationRules.password,
                      message: validationRules.newPasswordMessage,
                    },
                  })}
                />
                <span
                  className="password_view absolute top-[42px] right-[20px] cursor-pointer"
                  onClick={changeIcon2}
                >
                  {icon2 ? (
                    <AiFillEyeInvisible className="text-gray-500 dark:text-white" />
                  ) : (
                    <AiFillEye className="text-gray-500 dark:text-white" />
                  )}
                </span>
              </div>
            </div>

            {/* CONFIRM PASSWORD */}
            <div className="p-4">
              <div className="relative group">
                <OInputField
                  wrapperClassName="relative z-0 w-full group"
                  type={icon3 ? "password" : "text"}
                  name="confirm_password"
                  id="confirm_password"
                  placeholder={t("ENTER_CONFIRM_PASSWORD")}
                  errors={errors}
                  inputLabel={
                    <>
                      {t("CHANGE_PASSWORD_CONFIRM_PASSWORD")}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  onInput={(e) => preventMaxInput(e, 15)}
                  register={register("confirm_password", {
                    required: "Please enter confirm password.",
                    pattern: {
                      value: validationRules.password,
                      message: validationRules.confirmPasswordMessage,
                    },
                    validate: (val) => {
                      if (watch("newPassword") !== val) {
                        return "Confirm Password field is not matching with new password field.";
                      }
                    },
                  })}
                />
                <span
                  className="password_view absolute top-[42px] right-[20px] cursor-pointer"
                  onClick={changeIcon3}
                >
                  {icon3 ? (
                    <AiFillEyeInvisible className="text-gray-500 dark:text-white" />
                  ) : (
                    <AiFillEye className="text-gray-500 dark:text-white" />
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* SUBMIT BUTTON */}
          <div className="flex justify-center mt-6 mb-4">
            {changePasswordLoading ? (
              <motion.button
                whileTap={{ scale: 0.95 }}
                className="bg-gradientTo text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150"
              >
                <div className="spinner-container">
                  <div className="loading-spinner" />
                </div>
              </motion.button>
            ) : (
              <motion.div whileHover={{ scale: 1.05 }}>
                <OButton
                  disabled={!isDirty}
                  label={<>{t("O_SUBMIT")}</>}
                  type="submit"
                  title={t("O_SUBMIT")}
                />
              </motion.div>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default ChangePassword;
